import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ScanFace, Trash2, Plus, ShieldCheck } from "lucide-react";
import { enrolPasskey, passkeyError, passkeysSupported } from "@/lib/passkeys";
import { timeAgo } from "@/lib/format";

/** Profile → Security: turn Face ID / Touch ID sign-in on for this device. */
export const PasskeyCard = () => {
  const [creds, setCreds] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const supported = passkeysSupported();

  const load = useCallback(async () => {
    const { data } = await api.get("/webauthn/credentials");
    setCreds(data.credentials);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      await enrolPasskey(label || undefined);
      setLabel("");
      toast.success("Face ID sign-in is on", { description: "Next time, tap Face ID on the sign-in screen." });
      load();
    } catch (e) { toast.error("Couldn't set up Face ID", { description: passkeyError(e) }); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/webauthn/credentials/${id}`);
      toast.success("Device removed");
      load();
    } catch (e) { toast.error("Couldn't remove that device", { description: passkeyError(e) }); }
  };

  return (
    <Card className="p-5 shadow-premium" data-testid="passkey-card">
      <div className="flex items-center gap-2 text-sm font-medium">
        <ScanFace className="h-4 w-4 text-[color:var(--tharwy-indigo)]" />Face ID &amp; Touch ID
      </div>
      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
        Sign in with your face or fingerprint instead of typing a password. Your biometric never
        leaves your device — Tharwy only keeps a public key, and your password still works as a backup.
      </p>

      {!supported ? (
        <p className="mt-4 text-xs font-medium text-muted-foreground" data-testid="passkey-unsupported">
          This browser doesn't support Face ID sign-in. Try Safari on iPhone or Mac, or Chrome with Windows Hello.
        </p>
      ) : (
        <>
          {creds.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="passkey-list">
              {creds.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                  data-testid={`passkey-row-${c.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />{c.label}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Added {timeAgo(c.created_at)}
                      {c.last_used_at ? ` · last used ${timeAgo(c.last_used_at)}` : " · not used yet"}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(c.id)}
                    data-testid={`passkey-remove-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Input placeholder="Name this device (optional)" value={label}
              onChange={(e) => setLabel(e.target.value)} data-testid="passkey-label-input" />
            <Button onClick={add} disabled={busy} className="shrink-0" data-testid="passkey-add-btn">
              <Plus className="h-4 w-4 me-1.5" />{busy ? "Waiting for device..." : creds.length ? "Add this device" : "Enable Face ID"}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};
